// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/QuestionForm.test.tsx
import { Text } from "@radix-ui/themes";
import cx from "classnames";

import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";

type ChoiceRowBaseProps = {
  label: string;
  description?: string;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /**
   * Default `question-option`. QuestionForm passes
   * `question-option-{questionIndex}-{choiceNumber}` so each choice is unique.
   */
  "data-testid"?: string;
};

// why: number and numberTestId travel together — a fallback id with no question index collides across cards
export type ChoiceRowProps = ChoiceRowBaseProps &
  (
    | {
        /** 1-based answer index. Omit on rows that are not answer choices. */
        number: number;
        /** Unique testid for the number circle. */
        numberTestId: string;
      }
    | {
        number?: undefined;
        numberTestId?: undefined;
      }
  );

function choiceAccessibleName(
  number: number,
  label: string,
  description?: string,
): string {
  const head = `${number}. ${label}`;
  const detail = description?.trim();
  return detail ? `${head}, ${detail}` : head;
}

/**
 * Slot option row — Radix RadioCards select treatment:
 * resting fill/stroke-free, selected = 2px accent outline (not a thick glow ring).
 * why: ring-4/50 on selected felt off vs Themes RadioCards `outline: 2px solid`.
 */
export function ChoiceRow({
  label,
  description,
  selected = false,
  disabled = false,
  onClick,
  "data-testid": dataTestId = "question-option",
  number,
  numberTestId,
}: ChoiceRowProps) {
  // why: both must be set — numbering a row with no badge id used to fall back to a shared testid
  const numberedChoice =
    number != null && numberTestId != null ? { number, numberTestId } : null;
  return (
    <button
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.choiceRow)}
      data-testid={dataTestId}
      type="button"
      disabled={disabled}
      aria-pressed={selected}
      aria-label={
        numberedChoice
          ? choiceAccessibleName(numberedChoice.number, label, description)
          : undefined
      }
      onClick={onClick}
      className={cx(
        // why: min-w-0 so a long answer can truncate inside the option track.
        "relative flex w-full min-w-0 flex-col items-start gap-0.5 overflow-hidden rounded-lg border border-transparent bg-transparent px-3 py-2 text-left outline-none",
        // Radix RadioCards: checked → 2px accent outline; focus-visible is separate.
        selected && "outline outline-2 outline-puntt-accent-9",
        !disabled &&
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-puntt-accent-8",
        selected && !disabled && "focus-visible:outline-puntt-accent-10",
        !selected && !disabled && "hover:bg-puntt-neutral-gray-3",
        disabled && "cursor-default opacity-40",
        !disabled && "cursor-pointer",
      )}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">
        {numberedChoice ? (
          <span
            data-testid={numberedChoice.numberTestId}
            // why: aria-hidden — the button name already leads with this number, so AT would announce it twice
            aria-hidden="true"
            className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full border border-puntt-neutral-gray-6 px-1 text-xs font-medium tabular-nums leading-none text-puntt-neutral-gray-11"
          >
            {numberedChoice.number}
          </span>
        ) : null}
        <Text
          as="p"
          size="2"
          weight="medium"
          className="w-full min-w-0 truncate text-puntt-neutral-gray-11"
        >
          {label}
        </Text>
      </span>
      {description ? (
        <Text
          as="span"
          size="1"
          className="w-full min-w-0 whitespace-normal text-puntt-neutral-gray-11"
        >
          {description}
        </Text>
      ) : null}
    </button>
  );
}
